import { useCallback, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
    ColumnFiltersState,
    VisibilityState,
    SortingState,
    ColumnOrderState
} from '@tanstack/react-table';
import {
    selectSortConfig,
    selectTasksColumnFilters,
    selectTasksColumnOrder,
    selectTasksColumnVisibility,
    setSortConfig,
    setTasksColumnFilters,
    setTasksColumnOrder,
    setTasksColumnVisibility
} from '~/reducers/liveDispatchTableSlice';
import constants from '~/utils/constants';
import { columnIdAndSortablePropertyKeysMapping } from '../types';
import { LiveDispatchTaskTableColumnIds as columnIds } from '../useLiveDispatchTableConfig/columnDef';
import { isEmpty } from 'lodash';

export const useLiveDispatchTableStates = () => {
    const columnFilters = useSelector(selectTasksColumnFilters);
    const columnOrder = useSelector(selectTasksColumnOrder);
    const columnVisibility = useSelector(selectTasksColumnVisibility);
    const sortConfig = useSelector(selectSortConfig);

    const dispatch = useDispatch();

    const setColumnFilters = useCallback(
        (payload: ColumnFiltersState) => {
            dispatch(setTasksColumnFilters(payload));
        },
        [dispatch]
    );

    const setColumnOrder = useCallback(
        (payload: ColumnOrderState) => {
            dispatch(setTasksColumnOrder(payload));
        },
        [dispatch]
    );

    const setColumnVisibility = useCallback(
        (payload: VisibilityState) => {
            dispatch(setTasksColumnVisibility(payload));
        },
        [dispatch]
    );

    const setSorting = useCallback(
        (payload: SortingState) => {
            dispatch(setSortConfig(payload));
        },
        [dispatch]
    );

    const onApplySorting = useCallback(
        (id: string, sortingType: string) => {
            const convertedId = columnIdAndSortablePropertyKeysMapping[id];
            const isSortDescending = sortingType === constants.descending;
            setSorting([
                {
                    id: convertedId,
                    desc: isSortDescending
                }
            ]);
        },
        [setSorting]
    );

    const onClearSorting = useCallback(() => {
        setSorting([]);
    }, [setSorting]);

    const onClearColumnOrder = useCallback(() => {
        setColumnOrder(columnIds);
    }, [setColumnOrder]);

    const onClearColumnVisibility = useCallback(() => {
        const visibleColumns = columnIds.map((columnId) => [columnId, true]);
        setColumnVisibility(Object.fromEntries(visibleColumns));
    }, [setColumnVisibility]);

    useEffect(() => {
        if (!columnOrder.length || columnOrder.length !== columnIds.length) {
            onClearColumnOrder();
        }
    }, [columnOrder, onClearColumnOrder]);

    useEffect(() => {
        if (
            isEmpty(columnVisibility) ||
            Object.keys(columnVisibility).length !== columnIds.length
        ) {
            onClearColumnVisibility();
        }
    }, [columnVisibility, onClearColumnVisibility]);

    return {
        columnFilters,
        columnOrder,
        columnVisibility,
        sorting: sortConfig,
        setColumnFilters,
        setColumnOrder,
        setColumnVisibility,
        setSorting,
        onApplySorting,
        onClearSorting,
        onClearColumnOrder,
        onClearColumnVisibility
    };
};
